import axios from 'axios';
import { UseQueryOptions, UseQueryResult, useQuery } from 'react-query';
import { AllAuthUserProfile, fetchProfiles } from '../auth/profileApi';

export const ALL_AUTH_PROFILES_QUERY_KEY = ['allauth', 'profiles'] as const;

const retryProfileFetch = (failureCount: number, err: Error): boolean => {
  const status = axios.isAxiosError(err) ? err.response?.status : undefined;
  return status !== 401 && status !== 404 && failureCount < 2;
};

export const useAllAuthProfiles = (
  options?: Omit<
    UseQueryOptions<AllAuthUserProfile[], Error>,
    'queryKey' | 'queryFn'
  >,
): UseQueryResult<AllAuthUserProfile[], Error> =>
  useQuery<AllAuthUserProfile[], Error>(
    ALL_AUTH_PROFILES_QUERY_KEY,
    fetchProfiles,
    {
      retry: retryProfileFetch,
      // Swallow errors here so a background fetch from the user menu does not
      // surface the global query snackbar. Callers that need 401 handling
      // (the profile selector) pass their own onError.
      onError: () => undefined,
      ...options,
    },
  );

export const getActiveProfiles = (
  profiles: AllAuthUserProfile[] | undefined,
): AllAuthUserProfile[] => (profiles ?? []).filter((profile) => profile.active);

/** True when the user can meaningfully leave the current profile for another. */
export const useCanSwitchProfile = (): boolean => {
  const { data } = useAllAuthProfiles();
  return getActiveProfiles(data).length > 1;
};
